import React from 'react';
import { Row, Col, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { IconShare, IconBrandFacebook, IconBrandBluesky, IconBrandWhatsapp, IconMail } from '@tabler/icons-react';
import ShareButton from '../SocialShareButton/SocialShareButton';
import classes from './SocialShareBar.module.scss';

const DEFAULT_SERVICES = [
    'whatsApp',
    'facebook',
    'email',
    'facebookMessenger',
    'pinterest',
    'telegram',
    'linkedin',
    'xing',
    'bluesky',
    'instagram',
];

const SocialShareBar = ({
    headline,
    shareText,
    shareTitle,
    mailSubject,
    mailBody,
    url,
    alignment = 'center',
    displayMode = 'flat',
    services = DEFAULT_SERVICES,
    dropdownLabel = 'Mehr Stimmen sammeln? Jetzt teilen!',
    iconSize = 48,
    iconColor = '#b4b4b4',
    style = {},
}) => {
    const activeServices = Array.isArray(services) && services.length > 0 ? services : DEFAULT_SERVICES;

    const alignClass = alignment === 'left' ? 'text-start' : alignment === 'right' ? 'text-end' : 'text-center';
    const justifyClass =
        alignment === 'left'
            ? 'justify-content-start'
            : alignment === 'right'
              ? 'justify-content-end'
              : 'justify-content-center';

    if (displayMode === 'dropdown') {
        return (
            <div className={`social-share-dropdown-wrapper w-100 ${alignClass}`} style={style}>
                {headline && <h4 className="social-share-headline mb-3">{headline}</h4>}
                <UncontrolledDropdown direction="down" className="d-inline-block">
                    <DropdownToggle color="primary" outline>
                        {dropdownLabel}
                        <IconShare className="ps-1" size={20} />
                    </DropdownToggle>
                    <DropdownMenu end className="mt-1 p-0" style={{ boxShadow: '0 3px 4px 0px rgb(0 0 0 / 25%)' }}>
                        <Row className="g-0">
                            {activeServices.map((service, index) => (
                                <Col xs="6" key={index} className={classes.DropdownItemContainer || ''}>
                                    <DropdownItem className="btn-light btn" toggle={false}>
                                        <ShareButton
                                            shareType={service}
                                            shareTitle={shareTitle}
                                            shareText={shareText}
                                            shareUrl={url}
                                            mailSubject={mailSubject}
                                            mailBody={mailBody}
                                        />
                                    </DropdownItem>
                                    <DropdownItem divider className="m-0" />
                                </Col>
                            ))}
                        </Row>
                    </DropdownMenu>
                </UncontrolledDropdown>
            </div>
        );
    }

    if (displayMode === 'flat') {
        return (
            <div className={`social-share-flat-wrapper w-100 ${alignClass}`}>
                {headline && <h4 className="social-share-headline mb-3">{headline}</h4>}
                <Row className={`g-2 ${justifyClass}`}>
                    {activeServices.map((service, index) => (
                        <Col key={index} xs="6" sm="6" md="auto" className="flex-grow-1 flex-md-grow-0">
                            <ShareButton
                                shareType={service}
                                shareTitle={shareTitle}
                                shareText={shareText}
                                shareUrl={url}
                                mailSubject={mailSubject}
                                mailBody={mailBody}
                                variant="flat"
                            />
                        </Col>
                    ))}
                </Row>
            </div>
        );
    }

    // displayMode === 'bar' (Icon-only bar)
    const currentUrl =
        url || (typeof window !== 'undefined' ? window.location.href : process.env.REACT_APP_SITE_URL || '');
    const defaultText = shareText || 'Jetzt abstimmen!';
    const subject = mailSubject || defaultText;
    const body = mailBody ? `${mailBody}\n${currentUrl}` : `${defaultText}\n${currentUrl}`;

    const encodedUrl = encodeURIComponent(currentUrl);
    const encodedText = encodeURIComponent(defaultText);
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);

    return (
        <div className={`social-share-bar-wrapper w-100 ${alignClass}`}>
            {headline && <h4 className="social-share-headline mb-3">{headline}</h4>}
            <div className={`d-flex align-items-center ${justifyClass} gap-3 flex-wrap`}>
                <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.SocialShareIconLink}
                    title="Auf Facebook teilen"
                    aria-label="Auf Facebook teilen"
                >
                    <IconBrandFacebook color={iconColor} size={iconSize} />
                </a>
                <a
                    href={`whatsapp://send?text=${encodeURIComponent(`${defaultText} ${currentUrl}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${classes.SocialShareIconLink} d-lg-none`}
                    title="Über WhatsApp teilen"
                    aria-label="Über WhatsApp teilen"
                >
                    <IconBrandWhatsapp color={iconColor} size={iconSize} />
                </a>
                <a
                    href={`mailto:?subject=${encodedSubject}&body=${encodedBody}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.SocialShareIconLink}
                    title="Per E-Mail teilen"
                    aria-label="Per E-Mail teilen"
                >
                    <IconMail color={iconColor} size={iconSize} />
                </a>
                <a
                    href={`https://bsky.app/intent/compose?text=${encodedText}%0A%0A${encodedUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.SocialShareIconLink}
                    title="Auf Bluesky teilen"
                    aria-label="Auf Bluesky teilen"
                >
                    <IconBrandBluesky color={iconColor} size={iconSize} />
                </a>
            </div>
        </div>
    );
};

export default SocialShareBar;
